import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { showNotification } from 'actions/notification';
import { usePostTicket } from 'actions/chatbot';
import ChatArea from './chat-area';
import {
  Container,
  SetupPanel,
  ChatPanel,
  EmptyChat,
  SetupLabel,
  SetupInput,
  SetupSelect,
  StartButton,
  DraftToggleLabel,
  DraftToggleText,
  DraftHint,
  ErrorText,
  ResetButton,
  TicketInfo,
  DraftModeTag,
} from './styled-comps';

function TestChatTab({ botConfig, channels = [], selectedChannel }) {
  const dispatch = useDispatch();
  const [userId, setUserId] = useState('');
  // user_type and channel_name derived from selected channel
  const getSelectedChannel = () => channels.find(c => (c.id || c.name) === channelId);
  const getUserType = () => {
    const ch = getSelectedChannel();
    return ch ? ch.user_type : 'PARENT';
  };
  const getChannelName = () => {
    const ch = getSelectedChannel();
    return ch ? ch.name : undefined;
  };
  const [channelId, setChannelId] = useState(selectedChannel || '');
  const [useDraft, setUseDraft] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const { post: postTicket } = usePostTicket({
    onComplete: (errorMessage, data) => {
      setStarting(false);
      if (errorMessage) {
        setError(errorMessage);
        dispatch(
          showNotification({
            text: 'Failed to create test ticket',
            duration: 5,
            showCloseIcon: true,
          }),
        );
      } else {
        // POST /chatbot/tickets/ returns { ticket: {...} } in `data`
        setTicket(data?.ticket || data);
      }
    },
  });

  useEffect(() => {
    if (selectedChannel) {
      setChannelId(selectedChannel);
    }
  }, [selectedChannel]);

  const handleStart = () => {
    if (!userId.trim()) {
      setError('User ID is required');
      return;
    }

    setStarting(true);
    setError('');

    // is_test is hardcoded to true on the gateway side (every ticket
    // created via leap-admin is test by definition). The "Test with draft
    // changes" checkbox controls use_draft only.
    postTicket({
      caller_user_id: userId.trim(),
      user_type: getUserType(),
      channel_name: getChannelName(),
      use_draft: useDraft,
    });
  };

  const handleReset = () => {
    setTicket(null);
  };

  return (
    <Container>
      <SetupPanel>
        <div>
          <SetupLabel>User ID</SetupLabel>
          <SetupInput
            value={userId}
            onChange={e => setUserId(e.target.value)}
            placeholder="Enter user ID"
            disabled={!!ticket}
          />
        </div>
        {channels.length > 0 && (
          <div>
            <SetupLabel>Channel</SetupLabel>
            <SetupSelect
              value={channelId}
              onChange={e => setChannelId(e.target.value)}
              disabled={!!ticket}
            >
              {channels.map(ch => (
                <option key={ch.id || ch.name} value={ch.id || ch.name}>
                  {ch.display_name || ch.name}
                </option>
              ))}
            </SetupSelect>
          </div>
        )}
        <div>
          <DraftToggleLabel>
            <input
              type="checkbox"
              checked={useDraft}
              onChange={e => setUseDraft(e.target.checked)}
              disabled={!!ticket}
            />
            <DraftToggleText>Test with draft changes</DraftToggleText>
          </DraftToggleLabel>
          {useDraft && <DraftHint>Draft mode: bot uses unpublished changes</DraftHint>}
        </div>
        {error && <ErrorText>{error}</ErrorText>}
        {!ticket ? (
          <StartButton
            label={starting ? 'Starting...' : 'Start Test Chat'}
            onClick={handleStart}
            disabled={starting || !userId.trim()}
          />
        ) : (
          <ResetButton label="Reset" onClick={handleReset} />
        )}
        {ticket && (
          <TicketInfo>
            Ticket: {ticket.id}
            {useDraft && (
              <>
                <br />
                <DraftModeTag>Draft mode</DraftModeTag>
              </>
            )}
          </TicketInfo>
        )}
      </SetupPanel>
      <ChatPanel>
        {ticket ? (
          <ChatArea ticket={ticket} />
        ) : (
          <EmptyChat>Configure and start a test chat</EmptyChat>
        )}
      </ChatPanel>
    </Container>
  );
}

export default TestChatTab;
